// Microsoft Teams connect (Kjuup.io) on the web: popup, return handling, view state and error texts
// for the Teams settings page, plus the one place that redeems a completion code. Ported from the
// mobile app's utils/teamsConnect.js (in-app browser + kjuup:// deep link there).
//
// Connect flow on the web (backend TeamsAccountController):
//   1. GET /teams/accounts/auth-url?returnTo=<origin>, open it in a popup
//   2. the backend callback page posts { type: 'kjuup-teams-oauth', success, code, completionCode }
//      to exactly that origin and closes; without an opener it goes to
//        <origin>/profile-setting?section=teams&teams=complete&code=<completionCode>
//        <origin>/profile-setting?section=teams&teams=error&code=<errorCode>
//   3. POST /teams/accounts/complete { code } while signed in (10 minutes, one use only)
//   4. import progress arrives over the socket ('teamsAccount') or GET /teams/accounts/:id/status
//
// The same result can arrive twice (postMessage and a reload with the query), so handleTeamsReturn
// redeems each code once and shares the result. Redeemed codes are also kept in localStorage (a
// short, expiring list of digests), so a reload of the return URL does not redeem again.
import { completeTeamsConnect } from "../../helper/teamsApi";
import { teamsText } from "./teamsCore";

/** window events. */
export const TEAMS_ACCOUNT_EVENT = "kjuup:teamsAccount"; // socket 'teamsAccount' payload
export const TEAMS_CHATS_CHANGED_EVENT = "kjuup:teamsChatsChanged"; // socket 'teamsChatsChanged' payload
export const TEAMS_CONNECT_RESULT_EVENT = "kjuup:teamsConnectResult"; // { ok, code, message, account }

/** postMessage type of the backend's OAuth result page. */
export const TEAMS_OAUTH_MESSAGE = "kjuup-teams-oauth";

const emit = (name, detail) => {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch (_) {
    /* old browser without CustomEvent */
  }
};

/**
 * `?section=teams&teams=complete&code=…` → { kind: 'complete', code }
 * `?section=teams&teams=error&code=…`    → { kind: 'error', code }
 * Anything else → null. Accepts a search string, a URL or URLSearchParams.
 */
export function parseTeamsReturn(search) {
  let params;
  try {
    if (search instanceof URLSearchParams) params = search;
    else if (typeof search === "string" && /^https?:\/\//i.test(search)) params = new URL(search).searchParams;
    else if (typeof search === "string") params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    else return null;
  } catch (_) {
    return null;
  }
  const kind = params.get("teams");
  const code = params.get("code");
  if (kind === "complete") return code ? { kind: "complete", code } : null;
  if (kind === "error") return { kind: "error", code: code || "failed" };
  return null;
}

/**
 * The OAuth result page's postMessage → { kind, code } (or null when it is not one). A success
 * carries the completion code; an older backend without it reports 'failed'.
 */
export function teamsReturnFromMessage(data) {
  if (!data || typeof data !== "object" || data.type !== TEAMS_OAUTH_MESSAGE) return null;
  if (data.success) {
    return data.completionCode ? { kind: "complete", code: String(data.completionCode) } : { kind: "error", code: "failed" };
  }
  return { kind: "error", code: data.code ? String(data.code) : "failed" };
}

/** Toast when Disconnect failed (the backend's own text is English / German only). */
export const teamsDisconnectErrorMessage = (strings) =>
  teamsText(strings, "teams_err_disconnect", "Microsoft Teams could not be disconnected. Please try again.");

/** User-facing text for a connect / complete / sync error code (backend message as the fallback). */
export function teamsErrorMessage(code, serverMessage, strings) {
  const tr = (k, f) => teamsText(strings, k, f);
  switch (code) {
    case "wrong_user":
      return tr("teams_err_wrong_user", "This sign-in was started by a different Kjuup user. Please connect again.");
    case "invalid_state":
    case "state_expired":
    case "missing_code":
      return tr("teams_err_expired", "The sign-in has expired. Please connect again.");
    case "not_entitled":
      return tr("teams_err_not_entitled", "Microsoft Teams is part of Kjuup.io. Activate Kjuup.io to connect.");
    case "consent_required":
      return tr("teams_err_consent_required", "Your IT admin must approve Kjuup for Microsoft Teams first.");
    case "policy_blocked":
      return tr("teams_err_policy_blocked", "Your organization's policy does not allow connecting Kjuup to Microsoft Teams.");
    case "personal_account_not_supported":
      return tr("teams_err_personal_account", "Personal Microsoft accounts are not supported. Sign in with your work or school account.");
    case "access_denied":
      return tr("teams_err_cancelled", "The Microsoft sign-in was cancelled.");
    case "already_linked":
      return tr("teams_err_already_linked", "This Microsoft account is already connected to another Kjuup user.");
    case "different_account":
      // Older backends (one account per user).
      return tr("teams_err_different_account", "A different Microsoft account is already connected. Disconnect it first.");
    case "disconnect_in_progress":
      return tr("teams_err_disconnect_in_progress", "Your imported Teams chats are still being removed. Please try again in a few minutes.");
    case "temporarily_unavailable":
      return tr("teams_err_unavailable", "Microsoft Teams is temporarily unavailable. Please try again later.");
    case "not_enabled":
      return tr("teams_not_available", "Microsoft Teams is not available for your account yet.");
    case "too_soon":
      return tr("teams_err_too_soon", "A sync was started a few minutes ago. Please try again later.");
    case "not_connected":
      return tr("teams_reconnect_body", "Kjuup can no longer reach your Teams chats. They stay read-only until you reconnect.");
    default:
      return serverMessage || tr("teams_err_generic", "Microsoft Teams could not be connected. Please try again.");
  }
}

const STOPPED = ["disabled", "disconnecting", "disconnected"];
const IMPORTING = ["pending", "running", "recent_done"];

/**
 * What the Teams settings page shows for the user's account:
 *   none | removing | connecting | importing | connected | consent | policy | reconnect
 * `archived` is true when an earlier disconnect kept the chats read-only.
 */
export function teamsViewState(account) {
  if (!account || !account.status) return { state: "none", archived: false };
  const { status } = account;
  if (STOPPED.includes(status)) {
    if (status !== "disconnected" && account.disconnectMode === "purge" && !account.purgedAt) return { state: "removing", archived: false };
    return { state: "none", archived: account.disconnectMode === "keep_readonly" };
  }
  if (status === "consent_required") return { state: "consent", archived: false };
  if (status === "policy_blocked") return { state: "policy", archived: false };
  if (status === "auth_error" || status === "error" || status === "paused") return { state: "reconnect", archived: false };
  if (status === "connecting" || status === "connected") {
    const backfill = account.backfillState;
    if (status === "connecting" && (!backfill || backfill === "pending") && !(account.backfillProgress && account.backfillProgress.chatsTotal)) {
      return { state: "connecting", archived: false };
    }
    if (IMPORTING.includes(backfill)) return { state: "importing", archived: false };
    return { state: "connected", archived: false };
  }
  return { state: "reconnect", archived: false };
}

/**
 * Import progress from `backfillProgress` (all keys optional): value 0…1 or null when unknown,
 * done / total chats, the phase and the imported message count.
 */
export function teamsProgress(progress) {
  const p = progress && typeof progress === "object" ? progress : {};
  const total = Number(p.chatsTotal) || 0;
  const doneRaw = p.phase === "recent" && p.chatsRecentDone != null ? p.chatsRecentDone : p.chatsDone;
  const done = Math.max(0, Math.min(total, Number(doneRaw) || 0));
  return {
    value: total > 0 ? done / total : null,
    done,
    total,
    phase: p.phase || null,
    messages: Number(p.messagesImported) || 0,
  };
}

/** Merge a socket 'teamsAccount' account patch into the account on screen (same id only). */
export function mergeTeamsAccount(account, patch) {
  if (!account || !patch || patch.id == null || String(patch.id) !== String(account.id)) return account;
  const next = { ...account };
  ["status", "statusReason", "backfillState"].forEach((k) => {
    if (patch[k] !== undefined) next[k] = patch[k];
  });
  if (patch.backfillProgress !== undefined) {
    next.backfillProgress = patch.backfillProgress && typeof patch.backfillProgress === "object"
      ? { ...(account.backfillProgress || {}), ...patch.backfillProgress }
      : patch.backfillProgress;
  }
  return next;
}

// Codes being (or already) redeemed: code → Promise of the result. Error results are deduped by code
// for a short while, since the same result may arrive by postMessage and by the return URL at about
// the same moment. The window stays short: an error has no nonce, so a real second attempt that fails
// the same way (e.g. access_denied twice) must still be reported.
const redeemed = new Map();
const recentErrors = new Map();
const ERROR_DEDUPE_MS = 5000;

// ---- redeemed codes in this browser -------------------------------------------------------------

export const TEAMS_REDEEMED_KEY = "teams.redeemedCodes.v1";
const REDEEMED_MAX = 10;
const REDEEMED_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const defaultStorage = () => {
  try {
    return typeof window !== "undefined" && window.localStorage ? window.localStorage : null;
  } catch (_) {
    return null;
  }
};

// The stored form of a code: a short hash, so the browser never keeps the code itself.
export function teamsCodeDigest(code) {
  const str = String(code || "");
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
    h2 = Math.imul(h2 + c, 2246822519) >>> 0;
  }
  return `${h1.toString(36)}.${h2.toString(36)}.${str.length}`;
}

function readRedeemed(storage, now) {
  try {
    const raw = storage ? storage.getItem(TEAMS_REDEEMED_KEY) : null;
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((e) => e && typeof e.d === "string" && Number(e.at) > now - REDEEMED_TTL_MS) : [];
  } catch (_) {
    return [];
  }
}

/** True when this browser already redeemed `code`. */
export function wasTeamsCodeRedeemed(code, { storage = defaultStorage(), now = Date.now } = {}) {
  const d = teamsCodeDigest(code);
  return readRedeemed(storage, now()).some((e) => e.d === d);
}

/** Remembers `code` as redeemed: newest first, at most REDEEMED_MAX entries, expired ones dropped. */
export function rememberTeamsCode(code, { storage = defaultStorage(), now = Date.now } = {}) {
  const at = now();
  const d = teamsCodeDigest(code);
  const list = [{ d, at }, ...readRedeemed(storage, at).filter((e) => e.d !== d)].slice(0, REDEEMED_MAX);
  try {
    if (storage) storage.setItem(TEAMS_REDEEMED_KEY, JSON.stringify(list));
  } catch (_) {
    /* the in-memory guard still covers this page load */
  }
  return list;
}

/**
 * Handle a connect result once ({ kind: 'complete' | 'error', code }). Resolves to
 * { ok, code, message, account } (and emits TEAMS_CONNECT_RESULT_EVENT with it), or null when it
 * was handled already (in this page load, or earlier in this browser: then nothing is shown).
 */
export function handleTeamsReturn(link, { strings, complete = completeTeamsConnect, now = Date.now, storage = defaultStorage() } = {}) {
  if (!link || !link.kind) return Promise.resolve(null);
  if (link.kind === "error") {
    const at = recentErrors.get(link.code);
    if (at != null && now() - at < ERROR_DEDUPE_MS) return Promise.resolve(null);
    recentErrors.set(link.code, now());
    const result = { ok: false, code: link.code, message: teamsErrorMessage(link.code, null, strings), account: null };
    emit(TEAMS_CONNECT_RESULT_EVENT, result);
    return Promise.resolve(result);
  }
  if (!link.code) return Promise.resolve(null);
  if (redeemed.has(link.code)) return redeemed.get(link.code).then(() => null);
  const promise = Promise.resolve()
    .then(async () => {
      // A reload of the return URL: already redeemed, ignore it silently.
      if (wasTeamsCodeRedeemed(link.code, { storage, now })) return null;
      const r = await complete(link.code);
      // Remembered once the server answered (a network failure may still be retried).
      if (r && (r.ok || r.httpStatus != null || r.code)) rememberTeamsCode(link.code, { storage, now });
      else redeemed.delete(link.code);
      const result = r && r.ok
        ? { ok: true, code: "connected", message: teamsText(strings, "teams_connected_toast", "Microsoft Teams connected. Your chats are being imported."), account: r.account || null }
        : { ok: false, code: (r && r.code) || "failed", message: teamsErrorMessage(r && r.code, r && r.message, strings), account: null };
      emit(TEAMS_CONNECT_RESULT_EVENT, result);
      return result;
    })
    .catch((err) => {
      redeemed.delete(link.code);
      throw err;
    });
  redeemed.set(link.code, promise);
  return promise;
}

/** Test hook: forget redeemed codes. */
export function resetTeamsConnectState() {
  redeemed.clear();
  recentErrors.clear();
}

// ---- App-wide wiring (App.js socket) -------------------------------------------------------------

// Stable handlers, so a reconnect (App registers on every 'connect') replaces them instead of
// stacking copies, and only these handlers are removed: the Messages list has its own
// 'teamsChatsChanged' listener on the same socket.
const relayAccount = (res) => emit(TEAMS_ACCOUNT_EVENT, res);
const relayChatsChanged = (res) => emit(TEAMS_CHATS_CHANGED_EVENT, res);

/** Socket 'teamsAccount' (import progress) and 'teamsChatsChanged' → window events. */
export function relayTeamsSocketEvents(socket) {
  if (!socket || typeof socket.on !== "function") return;
  if (typeof socket.off === "function") {
    socket.off("teamsAccount", relayAccount);
    socket.off("teamsChatsChanged", relayChatsChanged);
  }
  socket.on("teamsAccount", relayAccount);
  socket.on("teamsChatsChanged", relayChatsChanged);
}
